import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLookup, type LookupItem } from '../lookups';
import { VariableCreateModal } from './VariableCreateModal';

// Sentinel select value for the "+ New variable…" option. The persisted
// shape only allows numeric ids so this can never clash with a real one.
const NEW_VARIABLE_OPTION = '__fb_new_variable__';

// Data Lookup editor (ModuleParameter.typeId === 13, valueType "simpledb").
//
// Mirrors the legacy ParameterEditorDbLookup in
// CustomerApplicationFlowNodeDetailsViewcontroller.js. The persisted JSON
// shape is the legacy contract so that flows edited here remain compatible
// with the old portal designer:
//
//   {
//     "simpleDbId": 123,
//     "dbName": "...",
//     "searchColumn": 1,
//     "searchValue": "$VAR.X",
//     "col_1": "$VAR.A",
//     "col_2": "$VAR.B"
//   }
//
// We also export the small helpers (DatabaseSelect, VariableSelect,
// DbColumnMappingRow, parseDbLookupValue, serializeDbLookupValue) so the
// Schedule Lookup editor can reuse them with its slightly different
// column-mapping semantics.

export interface DbLookupValue {
  simpleDbId: number | null;
  dbName: string | null;
  // searchColumn is 1-based; null when the user hasn't picked one yet.
  searchColumn: number | null;
  searchValue: string;
  // Sparse map keyed by 1-based column index. We only emit entries that
  // the user has explicitly bound to a variable.
  columnVariables: Record<number, string>;
}

const EMPTY_VALUE: DbLookupValue = {
  simpleDbId: null,
  dbName: null,
  searchColumn: null,
  searchValue: '',
  columnVariables: {},
};

export function parseDbLookupValue(raw: string): DbLookupValue {
  if (!raw) return { ...EMPTY_VALUE, columnVariables: {} };
  try {
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return { ...EMPTY_VALUE, columnVariables: {} };
    const cols: Record<number, string> = {};
    for (let i = 1; i <= 10; i++) {
      const cv = obj[`col_${i}`];
      if (cv != null && cv !== '') cols[i] = String(cv);
    }
    const simpleDbId =
      obj.simpleDbId != null && obj.simpleDbId !== ''
        ? Number(obj.simpleDbId)
        : null;
    const searchColumn =
      obj.searchColumn != null && obj.searchColumn !== ''
        ? Number(obj.searchColumn)
        : null;
    return {
      simpleDbId: Number.isFinite(simpleDbId) ? simpleDbId : null,
      dbName: typeof obj.dbName === 'string' ? obj.dbName : null,
      searchColumn: Number.isFinite(searchColumn) ? searchColumn : null,
      searchValue: obj.searchValue != null ? String(obj.searchValue) : '',
      columnVariables: cols,
    };
  } catch {
    return { ...EMPTY_VALUE, columnVariables: {} };
  }
}

// Serialize back to the legacy JSON shape. We omit fields we don't have a
// value for so the round-tripped JSON is as close as possible to what the
// legacy editor would emit.
export function serializeDbLookupValue(v: DbLookupValue, options?: { includeSearch?: boolean }): string {
  const includeSearch = options?.includeSearch ?? true;
  const out: Record<string, unknown> = {
    simpleDbId: v.simpleDbId,
    dbName: v.dbName,
  };
  if (includeSearch && v.simpleDbId != null) {
    out.searchColumn = v.searchColumn;
    out.searchValue = v.searchValue;
  }
  for (const [idx, varId] of Object.entries(v.columnVariables)) {
    if (varId) out[`col_${idx}`] = varId;
  }
  return JSON.stringify(out);
}

// Shared hooks for the database list + variable list. Both editors load
// these lookups; we forward to the generic `useLookup` hook so they
// participate in cache invalidation (focus-refresh + variable-create).
export function useDatabaseList(applicationId: number) {
  return useLookup('database', applicationId);
}

export function useVariableList(applicationId: number) {
  return useLookup('variable', applicationId);
}

interface DatabaseSelectProps {
  value: number | null;
  databases: LookupItem[];
  loading: boolean;
  readOnly: boolean;
  onChange: (id: number | null, name: string | null) => void;
}

export function DatabaseSelect({ value, databases, loading, readOnly, onChange }: DatabaseSelectProps) {
  return (
    <select
      className="fb-input"
      value={value == null ? '' : String(value)}
      disabled={readOnly || loading}
      onChange={(e) => {
        const next = e.target.value;
        if (!next) {
          onChange(null, null);
          return;
        }
        const id = Number(next);
        const match = databases.find((d) => String(d.id) === next);
        onChange(Number.isFinite(id) ? id : null, match?.name ?? null);
      }}
    >
      <option value="">{loading ? 'Loading…' : 'Select Database'}</option>
      {databases.map((d) => (
        <option key={String(d.id)} value={String(d.id)}>
          {d.name}
        </option>
      ))}
    </select>
  );
}

interface VariableSelectProps {
  value: string;
  variables: LookupItem[];
  loading: boolean;
  readOnly: boolean;
  placeholder?: string;
  onChange: (id: string) => void;
  /**
   * When set, the select offers a "+ New variable…" sentinel option that
   * opens the create-variable modal. The newly created id is committed
   * via `onChange` automatically. Pass the current applicationId so the
   * variable lands in the right app.
   */
  applicationId?: number;
}

// Variable picker that mirrors LookupEditor's grouped optgroup layout but
// takes a pre-loaded list so we don't refetch per-column.
export function VariableSelect({ value, variables, loading, readOnly, placeholder, onChange, applicationId }: VariableSelectProps) {
  const grouped = useMemo(() => groupByCategory(variables), [variables]);
  const [createOpen, setCreateOpen] = useState(false);
  const showNewOption = !readOnly && applicationId != null;
  return (
    <>
      <select
        className="fb-input"
        value={value ?? ''}
        disabled={readOnly || loading}
        onChange={(e) => {
          const v = e.target.value;
          if (v === NEW_VARIABLE_OPTION) {
            setCreateOpen(true);
            return;
          }
          onChange(v);
        }}
      >
        <option value="">{loading ? 'Loading…' : (placeholder ?? 'Select Variable')}</option>
        {grouped.map((g, gi) =>
          g.category ? (
            <optgroup key={`${gi}-${g.category}`} label={g.category}>
              {g.items.map((it) => (
                <option key={String(it.id)} value={String(it.id)}>
                  {it.name.trim()}
                </option>
              ))}
            </optgroup>
          ) : (
            g.items.map((it) => (
              <option key={String(it.id)} value={String(it.id)}>
                {it.name.trim()}
              </option>
            ))
          )
        )}
        {showNewOption && (
          <option value={NEW_VARIABLE_OPTION}>+ New variable…</option>
        )}
      </select>
      {showNewOption && (
        <VariableCreateModal
          open={createOpen}
          applicationId={applicationId}
          onCancel={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            onChange(id);
          }}
        />
      )}
    </>
  );
}

interface Group {
  category: string | null;
  items: LookupItem[];
}

function groupByCategory(items: LookupItem[]): Group[] {
  const result: Group[] = [];
  let currentCat: string | null | undefined = undefined;
  let currentItems: LookupItem[] = [];
  for (const it of items) {
    const cat = (it.category ?? null) as string | null;
    if (cat !== currentCat) {
      currentCat = cat;
      currentItems = [];
      result.push({ category: cat, items: currentItems });
    }
    currentItems.push(it);
  }
  return result;
}

interface ColumnMappingRowProps {
  // 1-based column index (matches legacy JSON keys col_1, col_2, ...).
  index: number;
  columnName: string;
  value: string;
  fixedLabel?: string;
  variables: LookupItem[];
  variablesLoading: boolean;
  readOnly: boolean;
  onChange: (varId: string) => void;
  /** Required when the row should offer "+ New variable…". */
  applicationId?: number;
}

// One row in the per-column variable-mapping table. When `fixedLabel`
// is set the variable picker is replaced by a disabled tag — used by
// Schedule Lookup for the implicit Date/Time columns.
export function DbColumnMappingRow({
  index,
  columnName,
  value,
  fixedLabel,
  variables,
  variablesLoading,
  readOnly,
  onChange,
  applicationId,
}: ColumnMappingRowProps) {
  return (
    <div className="fb-db-row">
      <span className="fb-db-row-idx">#{index}</span>
      <span className="fb-db-row-name" title={columnName}>{columnName}</span>
      {fixedLabel ? (
        <span className="fb-db-row-fixed">{fixedLabel}</span>
      ) : (
        <VariableSelect
          value={value}
          variables={variables}
          loading={variablesLoading}
          readOnly={readOnly}
          placeholder="Select Variable"
          onChange={onChange}
          applicationId={applicationId}
        />
      )}
    </div>
  );
}

// --- Data Lookup editor ------------------------------------------------

interface DbLookupEditorProps {
  value: string;
  readOnly: boolean;
  applicationId: number;
  commit: (next: string) => void;
}

export function DbLookupEditor({ value, readOnly, applicationId, commit }: DbLookupEditorProps) {
  const parsed = useMemo(() => parseDbLookupValue(value), [value]);
  const [draft, setDraft] = useState<DbLookupValue>(parsed);
  // Keep local state in sync if the saved value changes externally (e.g.
  // another user updates the flow). Without this the editor would lock the
  // user out of the new content once they've focused a field.
  useEffect(() => setDraft(parsed), [parsed]);

  const { items: databases, loading: dbLoading, error: dbError } = useDatabaseList(applicationId);
  const { items: variables, loading: varLoading } = useVariableList(applicationId);

  // The columns available for mapping come from the picked database's
  // metadata. Columns are 1-based; an empty `name` terminates the list
  // (mirrors the legacy `if (!column.name) break;` guard).
  const dbColumns = useMemo(() => {
    if (!draft.simpleDbId) return [] as Array<{ index: number; name: string }>;
    const db = databases.find((d) => Number(d.id) === draft.simpleDbId);
    if (!db?.columns) return [];
    const out: Array<{ index: number; name: string }> = [];
    for (let i = 0; i < db.columns.length; i++) {
      const c = db.columns[i];
      if (!c?.name) break;
      out.push({ index: i + 1, name: c.name });
    }
    return out;
  }, [databases, draft.simpleDbId]);

  // Auto-default the search column to col 1 when a database is freshly
  // picked and the user hasn't chosen a search column yet — matches the
  // legacy designer (`_.selectedColumn(_.columnList()[1].id)`).
  const effectiveSearchColumn = draft.searchColumn ?? (dbColumns.length > 0 ? 1 : null);

  const persist = useCallback(
    (next: DbLookupValue) => {
      const serialized = serializeDbLookupValue(next);
      if (serialized !== value) commit(serialized);
    },
    [commit, value],
  );

  const setDatabase = (id: number | null, name: string | null) => {
    const next: DbLookupValue = {
      simpleDbId: id,
      dbName: name,
      // Reset search + column mappings: the column set is database-specific
      // so old col_N entries would be meaningless against a new DB.
      searchColumn: id != null ? 1 : null,
      searchValue: '',
      columnVariables: {},
    };
    setDraft(next);
    persist(next);
  };

  const setSearchColumn = (col: number | null) => {
    const next = { ...draft, searchColumn: col };
    setDraft(next);
    persist(next);
  };

  const setSearchValue = (varId: string) => {
    const next = { ...draft, searchValue: varId };
    setDraft(next);
    persist(next);
  };

  const setColumnVariable = (colIndex: number, varId: string) => {
    const cols = { ...draft.columnVariables };
    if (varId) cols[colIndex] = varId;
    else delete cols[colIndex];
    const next = { ...draft, columnVariables: cols };
    setDraft(next);
    persist(next);
  };

  if (dbError) {
    return <div className="fb-error-inline">Failed to load databases: {dbError}</div>;
  }

  return (
    <div className="fb-db-editor">
      <div className="fb-db-row">
        <span className="fb-db-row-lbl">Database</span>
        <DatabaseSelect
          value={draft.simpleDbId}
          databases={databases}
          loading={dbLoading}
          readOnly={readOnly}
          onChange={setDatabase}
        />
      </div>

      {draft.simpleDbId != null && dbColumns.length > 0 && (
        <>
          <div className="fb-db-row">
            <span className="fb-db-row-lbl">Search column</span>
            <select
              className="fb-input"
              value={effectiveSearchColumn == null ? '' : String(effectiveSearchColumn)}
              disabled={readOnly}
              onChange={(e) => setSearchColumn(e.target.value ? Number(e.target.value) : null)}
            >
              {dbColumns.map((c) => (
                <option key={c.index} value={String(c.index)}>
                  #{c.index} {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="fb-db-row">
            <span className="fb-db-row-lbl">Search value</span>
            <VariableSelect
              value={draft.searchValue}
              variables={variables}
              loading={varLoading}
              readOnly={readOnly}
              placeholder="Select Variable"
              onChange={setSearchValue}
              applicationId={applicationId}
            />
          </div>

          <div className="fb-subsection">Column mappings</div>
          {dbColumns.map((c) => (
            <DbColumnMappingRow
              key={c.index}
              index={c.index}
              columnName={c.name}
              value={draft.columnVariables[c.index] ?? ''}
              variables={variables}
              variablesLoading={varLoading}
              readOnly={readOnly}
              onChange={(v) => setColumnVariable(c.index, v)}
              applicationId={applicationId}
            />
          ))}
        </>
      )}

      {draft.simpleDbId != null && dbColumns.length === 0 && !dbLoading && (
        <div className="fb-muted">No columns defined for this database.</div>
      )}
    </div>
  );
}
